/**
 * @render react
 * @name Video
 */

import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactPlayer from 'react-player';

import { withPolaris } from 'Components/protons/Polaris';

import './_style.scss';

/**
 * Video component
 *
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {Object} extraClassNames - Extra classes to be added to the component
 * @param {number} height - Set the height of the player. Optional. Default: 100%
 * @param {boolean} lazyLoading - Set to true or false to enable lazyLoading. Default: false
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {boolean} playing - Set to true or false to pause or play the media. Default: false
 * @param {string} url - The url of a video or song to play
 * @param {boolean} vimeo.vimeoAutoplay - Autoplay the video. Default: false
 * @param {number} youtube.youtubeAutoplay - Autoplay the video. Default: 0
 * @param {number} width - Set the width of the player. Optional. Default: 100%
 */
class Video extends Component {
  constructor(props) {
    super(props);

    this.state = {
      shouldRender: false,
    };

    this.containerRef = createRef();
    this.observer = null;
  }

  componentDidMount() {
    const {
      lazyLoading,
    } = this.props;

    if (!lazyLoading) {
      this.setState({ shouldRender: true });
      return;
    }

    this.lazyLoad();
  }

  initObserver(handler) {
    const options = {
      threshold: 0.1,
    };

    this.observer = new IntersectionObserver(handler, options);
  }

  lazyLoad() {
    const handleObserver = (entities) => {
      const [target] = entities;

      if (target.isIntersecting) {
        this.setState({ shouldRender: true });
        this.observer.unobserve(this.containerRef.current);
      }
    };

    if (!this.observer) {
      this.initObserver(handleObserver);
    }

    if (this.containerRef.current) {
      this.observer.observe(this.containerRef.current);
    }
  }

  render() {
    const {
      context: { cssPrefix },
      controls,
      extraClassNames,
      height,
      loadStyles,
      playing,
      url,
      vimeo: {
        vimeoAutoplay,
      },
      youtube: {
        youtubeAutoplay,
      },
      width,
    } = this.props; // eslint-disable-line

    const { shouldRender } = this.state;

    return (
      <div
        className={classNames({
          [`${cssPrefix}__video`]: loadStyles,
          [`${cssPrefix}__player-wrapper`]: true,
          ...extraClassNames,
        })}
        ref={this.containerRef}
      >
        {shouldRender && (
          <ReactPlayer
            className={`${cssPrefix}__react-player`}
            config={{
              youtube: {
                playerVars: {
                  autoplay: youtubeAutoplay,
                },
              },
              vimeo: {
                playerVars: {
                  autoplay: vimeoAutoplay,
                },
              },
            }}
            controls={controls}
            height={height || 'auto'}
            playing={playing}
            src={url}
            width={width || 'auto'}
          />
        )}
      </div>
    );
  }
}

/**
 * Valid prop types
 */
Video.propTypes = {
  context: PropTypes.shape(),
  controls: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lazyLoading: PropTypes.bool,
  loadStyles: PropTypes.bool,
  playing: PropTypes.bool,
  vimeo: PropTypes.shape({
    vimeoAutoplay: PropTypes.bool,
  }),
  youtube: PropTypes.shape({
    youtubeAutoplay: PropTypes.number,
  }),
  url: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

/** Default props */
Video.defaultProps = {
  context: {},
  controls: true,
  extraClassNames: {},
  height: null,
  lazyLoading: false,
  loadStyles: true,
  playing: false,
  vimeo: {
    vimeoAutoplay: false,
  },
  youtube: {
    youtubeAutoplay: 0,
  },
  width: null,
};

export default withPolaris(Video);
